import React, { useCallback, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Text, TextTypography } from 'components/Text/Text';
import { SmallButton } from 'components/SmallButton/SmallButton';
import { RoundCheckbox } from 'components/RoundCheckbox/RoundCheckbox';
import { useSelector } from 'react-redux';
import { registrationChecklistSelector } from 'state/registration/selector';
import { useAction } from 'core/store/store';
import { makeRegistrationSetChecklistAction } from 'state/registration/actions';
import i18n from '../../../../../i18n';

export interface StepsWidgetParams {
    onOpenExpanded: () => void;
}

export interface Steps {
    sticker: boolean;
    online: boolean;
    social: boolean;
    visibility: boolean;
    freeShipping: boolean;
    feedBack: boolean;
}

const useStyles = makeStyles(theme => ({
    container: {
        width: '100%',
        height: '100%',
        background: 'rgba(215, 248, 195, 0.7)',
        borderRadius: 20,
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up('sm')]: {
            height: 335,
        },
        [theme.breakpoints.up('md')]: {
            height: 223,
        },
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    checkboxes: {
        display: 'flex',
        marginTop: theme.spacing(4),
        flexDirection: 'column',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
        },
    },
    checkbox: {
        marginBottom: theme.spacing(0.5),
    },
    half: {
        flexGrow: 1,
    },
    desktopButton: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    mobileButton: {
        display: 'block',
        marginTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
}));

export const StepsWidget = (params: StepsWidgetParams) => {
    const classes = useStyles();
    const action = useAction();
    const { t } = i18n.useTranslation();
    const checklist = useSelector(registrationChecklistSelector);
    const [steps, setSteps] = useState<Steps>(
        checklist || {
            sticker: false,
            online: false,
            social: false,
            visibility: false,
            freeShipping: false,
            feedBack: false,
        },
    );

    const handleStepsChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSteps = { ...steps, [event.target.name]: event.target.checked };
            setSteps(newSteps);
            action(makeRegistrationSetChecklistAction, newSteps);
        },
        [setSteps, steps, action],
    );

    return (
        <div className={classes.container}>
            <div className={classes.title}>
                <Text typography={TextTypography.LEAD_ACCENT}>{t('vendor:dashboard.stepsWidget.title')}</Text>
                <div className={classes.desktopButton}>
                    <SmallButton
                        color={'#75C345'}
                        hoverColor={'rgb(102, 177, 57)'}
                        width={123}
                        onClick={params.onOpenExpanded}
                        text={'vendor:dashboard.stepsWidget.extended'}
                    />
                </div>
            </div>
            <div className={classes.checkboxes}>
                <div className={classes.half}>
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.sticker}
                        name={'sticker'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.sticker'}
                        strikeThroughIfChecked={true}
                    />
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.online}
                        name={'online'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.online'}
                        strikeThroughIfChecked={true}
                    />
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.social}
                        name={'social'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.social'}
                        strikeThroughIfChecked={true}
                    />
                </div>
                <div className={classes.half}>
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.visibility}
                        name={'visibility'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.visibility'}
                        strikeThroughIfChecked={true}
                    />
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.freeShipping}
                        name={'freeShipping'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.freeShipping'}
                        strikeThroughIfChecked={true}
                    />
                    <RoundCheckbox
                        className={classes.checkbox}
                        value={steps.feedBack}
                        name={'feedBack'}
                        handleChange={handleStepsChange}
                        label={'vendor:dashboard.stepsWidget.feedBack'}
                        strikeThroughIfChecked={true}
                    />
                </div>
            </div>
            <div className={classes.mobileButton}>
                <SmallButton
                    color={'#75C345'}
                    hoverColor={'rgb(102, 177, 57)'}
                    width={123}
                    onClick={params.onOpenExpanded}
                    text={'vendor:dashboard.stepsWidget.extended'}
                />
            </div>
        </div>
    );
};
